import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Users, 
  TrendingUp, 
  Target, 
  Handshake, 
  Mail, 
  Phone, 
  MapPin,
  Calendar,
  Award,
  Briefcase,
  GraduationCap
} from 'lucide-react';
import { personalInfo, professionalSummary, keyStrengths, skills, certifications, education } from '../mock/mockData';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    preferredDate: ''
  });

  const iconMap = {
    Users: Users,
    TrendingUp: TrendingUp,
    Target: Target,
    Handshake: Handshake
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    if (formData.name && formData.email && formData.preferredDate) {
      toast.success('Meeting request submitted! I will contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        preferredDate: ''
      });
    } else {
      toast.error('Please fill in all required fields.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-slate-900">{personalInfo.name}</h1>
            <nav className="flex gap-6">
              <a href="#about" className="text-slate-600 hover:text-blue-900 transition-colors font-medium">About</a>
              <a href="#strengths" className="text-slate-600 hover:text-blue-900 transition-colors font-medium">Strengths</a>
              <a href="#skills" className="text-slate-600 hover:text-blue-900 transition-colors font-medium">Skills</a>
              <a href="#contact" className="text-slate-600 hover:text-blue-900 transition-colors font-medium">Contact</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 to-slate-800 text-white py-24 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1600&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-block mb-4">
              <Badge className="bg-blue-500 text-white px-4 py-1 text-sm">Open to Opportunities</Badge>
            </div>
            <h2 className="text-5xl font-bold mb-4 leading-tight">{personalInfo.name}</h2>
            <p className="text-2xl text-blue-100 mb-6">{personalInfo.title}</p>
            <p className="text-lg text-blue-50 mb-8 leading-relaxed max-w-2xl">
              {personalInfo.tagline}
            </p>
            <div className="flex gap-4 flex-wrap">
              <Button 
                size="lg" 
                className="bg-white text-blue-900 hover:bg-blue-50 px-8 py-6 text-lg font-semibold shadow-lg transition-all hover:scale-105"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                <Calendar className="mr-2 h-5 w-5" />
                Schedule a Meeting
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-6 text-lg font-semibold transition-all"
                onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>
            <div className="mt-12 flex gap-6 text-blue-100">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>{personalInfo.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                <span>{personalInfo.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                <span>{personalInfo.phone}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About/Summary Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6">
                <Badge className="bg-blue-100 text-blue-900 px-3 py-1 mb-4">Professional Summary</Badge>
                <h3 className="text-4xl font-bold text-slate-900 mb-4">
                  {professionalSummary.headline}
                </h3>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {professionalSummary.description}
              </p>
              <div className="space-y-3">
                {professionalSummary.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="mt-1 bg-blue-100 rounded-full p-1">
                      <TrendingUp className="h-4 w-4 text-blue-900" />
                    </div>
                    <p className="text-slate-700 font-medium">{highlight}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl max-w-xs mx-auto">
                <img 
                  src="https://customer-assets.emergentagent.com/job_pedalino-resume/artifacts/gw724e7z_img1.webp" 
                  alt="Michael Pedalino - Professional Photo"
                  className="w-full h-[320px] object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-blue-900 text-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-3">
                  <Award className="h-10 w-10" />
                  <div>
                    <p className="text-3xl font-bold">15+</p>
                    <p className="text-blue-100">Years Experience</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Strengths Section */}
      <section id="strengths" className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="bg-blue-100 text-blue-900 px-3 py-1 mb-4">Core Competencies</Badge>
            <h3 className="text-4xl font-bold text-slate-900 mb-4">Key Strengths</h3>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Proven expertise across customer success, sales, and relationship management
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyStrengths.map((strength, index) => {
              const IconComponent = iconMap[strength.icon];
              return (
                <Card key={index} className="border-slate-200 hover:shadow-xl transition-all hover:-translate-y-1 bg-white">
                  <CardHeader>
                    <div className="bg-blue-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="h-7 w-7 text-blue-900" />
                    </div>
                    <CardTitle className="text-xl text-slate-900">{strength.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-600 leading-relaxed">
                      {strength.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Skills & Certifications Section */}
      <section id="skills" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Skills */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-blue-100 w-12 h-12 rounded-xl flex items-center justify-center">
                  <Briefcase className="h-6 w-6 text-blue-900" />
                </div>
                <h3 className="text-3xl font-bold text-slate-900">Professional Skills</h3>
              </div>
              <div className="flex flex-wrap gap-3">
                {skills.map((skill, index) => (
                  <Badge 
                    key={index} 
                    variant="outline" 
                    className="px-4 py-2 text-sm border-slate-300 text-slate-700 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Certifications & Education */}
            <div>
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-blue-100 w-12 h-12 rounded-xl flex items-center justify-center">
                    <Award className="h-6 w-6 text-blue-900" />
                  </div>
                  <h3 className="text-3xl font-bold text-slate-900">Certifications</h3>
                </div>
                <div className="space-y-3">
                  {certifications.map((cert, index) => (
                    <div key={index} className="flex items-center gap-3 text-slate-700">
                      <div className="w-2 h-2 bg-blue-900 rounded-full"></div>
                      <span>{cert}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-blue-100 w-12 h-12 rounded-xl flex items-center justify-center">
                    <GraduationCap className="h-6 w-6 text-blue-900" />
                  </div>
                  <h3 className="text-3xl font-bold text-slate-900">Education</h3>
                </div>
                <Card className="border-slate-200 bg-slate-50">
                  <CardContent className="pt-6">
                    <p className="font-semibold text-slate-900 mb-1">{education.degree}</p>
                    <p className="text-slate-600">{education.institution}</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact/Schedule Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-blue-900 to-slate-800 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="bg-blue-500 text-white px-3 py-1 mb-4">Let's Connect</Badge>
              <h3 className="text-4xl font-bold mb-4">Schedule a Meeting</h3>
              <p className="text-xl text-blue-100">
                Interested in discussing opportunities? Let's schedule a time to connect.
              </p>
            </div>

            <Card className="border-0 shadow-2xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-700 font-semibold">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="border-slate-300"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-700 font-semibold">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="border-slate-300"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-700 font-semibold">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="border-slate-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preferredDate" className="text-slate-700 font-semibold">
                        Preferred Date *
                      </Label>
                      <Input
                        id="preferredDate"
                        name="preferredDate"
                        type="date"
                        value={formData.preferredDate}
                        onChange={handleInputChange}
                        className="border-slate-300"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-700 font-semibold">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell me about the opportunity or what you'd like to discuss..."
                      value={formData.message}
                      onChange={handleInputChange}
                      className="border-slate-300 min-h-[120px]"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6 text-lg font-semibold transition-all hover:scale-[1.02]"
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Request Meeting
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-12 text-center">
              <p className="text-blue-100 mb-4">Or reach out directly:</p>
              <div className="flex justify-center gap-8 flex-wrap">
                <a href={`mailto:${personalInfo.email}`} className="flex items-center gap-2 text-white hover:text-blue-300 transition-colors">
                  <Mail className="h-5 w-5" />
                  <span className="font-medium">{personalInfo.email}</span>
                </a>
                <a href={`tel:${personalInfo.phone}`} className="flex items-center gap-2 text-white hover:text-blue-300 transition-colors">
                  <Phone className="h-5 w-5" />
                  <span className="font-medium">{personalInfo.phone}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>&copy; 2025 {personalInfo.name}. All rights reserved.</p>
            <div className="flex gap-6">
              <span className="text-sm">Professional. Dedicated. Results-Driven.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
